import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Panel, Empty } from "@/components/app/PageParts";
import { PropertyDialog } from "@/components/app/PropertyFields";
import { api, errorMessage } from "@/lib/api";
import { PROPERTY_TYPES } from "@/lib/format";

export default function Properties() {
  const { workspace, properties, reloadProperties } = useOutletContext();
  const [editing, setEditing] = useState(undefined);

  const remove = async (p) => {
    try { await api.delete(`/workspaces/${workspace.id}/properties/${p.id}`); toast.success("Property removed"); reloadProperties(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div data-testid="properties-page">
      <PageHeader eyebrow="Properties" title="Properties and projects" subtitle="Keep each home, listing or project separate. Documents, actions, deadlines and form drafts can be linked to a property."
        action={<Button onClick={() => setEditing(null)} data-testid="property-create-button" className="bg-navy hover:bg-navy-2"><Plus className="mr-1.5 h-4 w-4" /> Add property</Button>} />
      <Panel title={`Properties (${properties.length})`} testId="properties-list">
        {!properties.length ? <Empty testId="properties-empty">No properties yet. Add your first one.</Empty> : (
          <ul className="divide-y">
            {properties.map((p) => (
              <li key={p.id} data-testid={`property-row-${p.id}`} className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center">
                <MapPin className="hidden h-5 w-5 text-gold-dark sm:block" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-navy" data-testid="property-row-name">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    {[p.address, p.city, p.state].filter(Boolean).join(", ")}{p.county ? ` · ${p.county} County` : " · County not set"}
                    {p.property_type && ` · ${Object.fromEntries(PROPERTY_TYPES)[p.property_type]}`} · {p.timezone.replace(/_/g, " ")}
                    {p.migrated && <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">Moved from earlier workspace details</span>}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setEditing(p)} data-testid="property-edit-button"><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p)} data-testid="property-delete-button" className="text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
      {editing !== undefined && (
        <PropertyDialog key={editing?.id || "new"} workspaceId={workspace.id} property={editing} open onOpenChange={(o) => !o && setEditing(undefined)} onSaved={reloadProperties} />
      )}
    </div>
  );
}
